import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * The re:form Loyalty campaign import, as an operator sees it (YUKI-248).
 *
 * The importer has always written a per-campaign verdict — what it did with
 * each campaign and, when it did nothing, why — and the backend has always
 * served it. Until this hook, nothing read it: a campaign the till cannot
 * price simply had no promotion row, so it was invisible in Back Office and
 * the only trace was a log line on the server.
 */

/** What the importer did with a campaign on the last run. */
export type CampaignImportOutcome = 'imported' | 'updated' | 'unchanged' | 'archived' | 'skipped';

/**
 * Why a campaign is not priced by the till.
 *
 * Mirrors `SkipReason` in the backend mapper plus `code_taken`, which the
 * importer raises rather than the mapper. Kept as a union of known codes with
 * a `string` escape: a newer backend must render as an unlabelled code rather
 * than break the screen.
 */
export type CampaignSkipReason =
  | 'accrual_only'
  | 'unsupported_model'
  | 'unsupported_benefit'
  | 'unsupported_percent'
  | 'unsupported_condition'
  | 'unsupported_applicability'
  | 'unsupported_limits'
  | 'unsupported_field'
  | 'unsupported_schedule'
  | 'unsupported_rule_schema'
  | 'unsupported_scope'
  | 'no_catalogue_match'
  | 'conflicts_with'
  | 'not_active_in_loyalty'
  | 'expired_in_loyalty'
  | 'ended'
  | 'invalid_mapping'
  | 'invalid_definition'
  | 'code_taken'
  | (string & {});

export type CampaignImportEntry = {
  externalId: string;
  name: string;
  /** Loyalty's own campaign model, e.g. `fixed_percent`, `group_mix`. */
  model: string;
  outcome: CampaignImportOutcome;
  /** The BOS promotion code, when a row exists for the campaign. */
  code: string | null;
  reason: CampaignSkipReason | null;
  detail: string | null;
  /** Fields the mapper ignored, unknown skus, unmatched category codes. */
  notes: string[];
};

export type CampaignImportTransition = {
  externalId: string;
  name: string;
  kind: 'lost' | 'recovered' | 'changed' | 'appeared' | 'disappeared';
  from: { outcome: CampaignImportOutcome; reason: CampaignSkipReason | null } | null;
  to: { outcome: CampaignImportOutcome; reason: CampaignSkipReason | null } | null;
};

export type CampaignImportReport = {
  runAt: string;
  trigger: 'schedule' | 'manual';
  firstRun: boolean;
  fullResync: boolean;
  /** False = the delta was not drained; the next run carries on. */
  complete: boolean;
  registry: { version: string; count: number; staged: number; consistent: boolean };
  fetched: { snapshot: number; deltaRows: number; deltaPages: number };
  counts: Record<CampaignImportOutcome, number>;
  campaigns: CampaignImportEntry[];
  transitions: CampaignImportTransition[];
};

export type CampaignImportStatus = {
  schedule: { enabled: boolean; intervalMs: number };
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  registryVersion: string | null;
  /** Null until the first run has completed on this tenant. */
  report: CampaignImportReport | null;
};

const KEY = ['loyalty', 'campaign-import'] as const;

/** Whether the till prices a campaign at all. */
export function isPricedByTill(entry: CampaignImportEntry): boolean {
  return entry.outcome !== 'skipped' && entry.outcome !== 'archived';
}

/**
 * Reasons that mean Loyalty itself no longer runs the campaign. The till is
 * right not to price these, so they are not something an operator must act on.
 * `ended` is a campaign still marked active in Loyalty whose end date has
 * passed: Loyalty does not price it either, and counting it would keep the
 * promotions-list notice on for every finished campaign.
 */
const NOT_RUN_BY_LOYALTY: ReadonlySet<string> = new Set([
  'expired_in_loyalty',
  'not_active_in_loyalty',
  'ended',
]);

/**
 * A campaign Loyalty still offers that the till does not price (RL-419) —
 * the case where a retailer goes looking for a promotion that is not there.
 */
export function isLiveButNotPriced(entry: CampaignImportEntry): boolean {
  if (isPricedByTill(entry)) return false;
  return entry.reason === null || !NOT_RUN_BY_LOYALTY.has(entry.reason);
}

/**
 * `enabled: false` for a tenant without the Loyalty module, whose route
 * answers 404 — the promotions list asks only when there is something to ask.
 */
export function useLoyaltyCampaignImport(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: KEY,
    enabled: options.enabled ?? true,
    queryFn: async (): Promise<CampaignImportStatus> => {
      const res = await apiClient.get<CampaignImportStatus>('/loyalty/campaign-import');
      return res.data;
    },
  });
}

/**
 * Run the import now. Requires `promotions.admin`, where reading the status
 * needs only `promotions.read` — so a viewer sees the report without being
 * offered a button they cannot use.
 */
export function useRunLoyaltyCampaignImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/loyalty/campaign-import/run');
      return res.data as unknown;
    },
    onSuccess: async () => {
      // The run rewrites both the report and the promotion rows it imported.
      await Promise.all([
        qc.invalidateQueries({ queryKey: KEY }),
        qc.invalidateQueries({ queryKey: ['promotions'] }),
      ]);
    },
  });
}
